import React from 'react';
import { motion } from 'framer-motion';
import { PageHero, Section } from '../components/content/Content';
import {
    Sparkles,
    Layers,
    Compass,
    TrendingUp,
    Heart,
    Calendar,
    Activity,
    Zap,
    AlignCenter,
    Wifi,
    Combine,
    CheckCircle2,
    ArrowRight
} from 'lucide-react';

// --- Types ---
interface ServiceItem {
    id: string;
    title: string;
    price: string;
    duration: string;
    description: string;
    included: string[];
    bestFor: string;
    icon: React.ElementType;
}

// --- Data ---
const tarotServices: ServiceItem[] = [
    {
        id: 'tarot-single',
        title: 'Single Card Guidance',
        price: '₹700',
        duration: '15 min',
        description: 'Quick clarity on a specific question or the theme of your day.',
        included: ['1-Question Focus', 'Immediate Action Step', 'Digital Card Image'],
        bestFor: 'Quick check-ins or daily guidance',
        icon: Sparkles
    },
    {
        id: 'tarot-three',
        title: 'Three Card Spread',
        price: '₹1,500',
        duration: '30 min',
        description: 'Past, Present, Future or Situation, Action, Outcome insights.',
        included: ['3-Card Analysis', 'Context & Advice', 'Voice Note Summary'],
        bestFor: 'Understanding a specific situation',
        icon: Layers
    },
    {
        id: 'tarot-celtic',
        title: 'Celtic Cross Reading',
        price: '₹3,500',
        duration: '60 min',
        description: 'Deep dive into complex situations using the traditional 10-card spread.',
        included: ['10-Card In-depth Spread', 'Root Cause Analysis', 'Future Probabilities', 'Detailed PDF Report'],
        bestFor: 'Deep clarity on major life events',
        icon: Compass
    },
    {
        id: 'tarot-career',
        title: 'Career & Finance',
        price: '₹2,800',
        duration: '45 min',
        description: 'Strategic guidance for your professional path and financial energy.',
        included: ['Career Path Analysis', 'Financial Blockages', 'Opportunity Radar'],
        bestFor: 'Job changes or business decisions',
        icon: TrendingUp
    },
    {
        id: 'tarot-relationship',
        title: 'Relationship Reading',
        price: '₹2,800',
        duration: '45 min',
        description: 'Understanding dynamics between you and a partner or loved one.',
        included: ['Connection Energy', 'Mirroring Aspects', 'Growth Potential'],
        bestFor: 'Couples or navigating conflicts',
        icon: Heart
    },
    {
        id: 'tarot-year',
        title: 'Year Ahead Reading',
        price: '₹6,000',
        duration: '90 min',
        description: 'A comprehensive month-by-month guide for your upcoming year.',
        included: ['12-Month Forecast', 'Core Theme of the Year', 'Quarterly Focus Areas', 'Recorded Session'],
        bestFor: 'Birthdays or New Year planning',
        icon: Calendar
    }
];

const chakraServices: ServiceItem[] = [
    {
        id: 'chakra-assess',
        title: 'Assessment & Diagnosis',
        price: '₹1,500',
        duration: '30 min',
        description: 'Identify which of your energy centers are blocked or overactive.',
        included: ['Pendulum Scan', 'Symptom Correlation', 'Personalized Report'],
        bestFor: 'First-time energy work',
        icon: Activity
    },
    {
        id: 'chakra-single',
        title: 'Single Chakra Balancing',
        price: '₹2,200',
        duration: '45 min',
        description: 'Targeted healing for a specific issue (e.g., throat for communication).',
        included: ['Focused Energy Work', 'Specific Guided Meditation', 'Affirmation Set'],
        bestFor: 'Addressing acute physical/emotional issues',
        icon: Zap
    },
    {
        id: 'chakra-full',
        title: 'Full 7-Chakra Alignment',
        price: '₹4,500',
        duration: '90 min',
        description: 'Complete energetic reset balancing all seven major chakras.',
        included: ['Full Body Scan', 'Deep Energy Cleansing', 'Alignment Visualization', 'Crystal Therapy'],
        bestFor: 'Total burnout or feeling "off"',
        icon: AlignCenter
    },
    {
        id: 'chakra-remote',
        title: 'Distance/Remote Healing',
        price: '₹3,000',
        duration: '60 min',
        description: 'Powerful energy healing transmitted to you wherever you are.',
        included: ['Remote Connection', 'Live Audio Guidance', 'Post-Session Feedback'],
        bestFor: 'International clients or busy schedules',
        icon: Wifi
    },
    {
        id: 'chakra-tarot',
        title: 'Chakra Balancing w/ Tarot',
        price: '₹6,500',
        duration: '120 min',
        description: 'The ultimate transformation session combining insight and healing.',
        included: ['Chakra Tarot Spread', 'Energy Diagnosis', 'Full Healing Session', 'Integration Plan'],
        bestFor: 'Deep transformational shifts',
        icon: Combine
    }
];

// --- Components ---

const ServiceCard: React.FC<{ service: ServiceItem; index: number }> = ({ service, index }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(index * 0.05, 0.5), duration: 0.5 }}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            className="flex flex-col h-full border-t border-lavender-300 pt-6 group"
        >
            <div className="flex justify-between items-start mb-6">
                <div className="p-3 bg-lavender-100 rounded-full">
                    <service.icon className="w-6 h-6 text-lavender-700" aria-hidden="true" />
                </div>
                <div className="text-right">
                    <div className="text-lavender-700 font-bold text-lg">{service.price}</div>
                    <div className="text-plum-soft text-sm">{service.duration}</div>
                </div>
            </div>

            <h3 className="font-fraunces text-2xl mb-3">
                {service.title}
            </h3>

            <p className="text-plum-soft text-sm mb-6 flex-grow line-clamp-4">
                {service.description}
            </p>

            <div className="space-y-4 mb-8">
                {service.included.length > 0 && (
                    <div>
                        <h4 className="text-xs uppercase tracking-wider text-lavender-700 mb-2 font-bold">What's Included</h4>
                        <ul className="space-y-1">
                            {service.included.map((item, i) => (
                                <li key={i} className="flex items-start text-sm text-plum-soft">
                                    <CheckCircle2 className="w-4 h-4 text-lavender-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div>
                    <h4 className="text-xs uppercase tracking-wider text-lavender-700 mb-1 font-bold">Best For</h4>
                    <p className="text-sm text-plum italic">
                        {service.bestFor}
                    </p>
                </div>
            </div>

            <button className="w-full mt-auto py-3 px-6 rounded-full border border-lavender-300 text-lavender-700 font-bold hover:bg-lavender-50 transition-colors flex items-center justify-center gap-2">
                Book This Session
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
        </motion.div>
    );
};

// --- Page Component ---

const Services: React.FC = () => {
    return (
        <>

            <PageHero
                title="Your path to"
                accent="clarity & balance"
                subtitle="Choose from Tarot readings, chakra healing or combined sessions tailored to your journey."
            />

            {/* Tarot Services */}
            <Section id="tarot" title="Tarot Readings" subtitle="Unveil the wisdom of the cards">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {tarotServices.map((service, index) => (
                        <ServiceCard key={service.id} service={service} index={index} />
                    ))}
                </div>
            </Section>

            {/* Chakra Services */}
            <Section id="chakra" tint title="Chakra Healing" subtitle="Align your energy centers">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 justify-center">
                    {chakraServices.map((service, index) => (
                        <ServiceCard key={service.id} service={service} index={index + 6} /> // Offset index for staggered animation
                    ))}
                </div>
            </Section>

            {/* Ready to Book CTA (Optional reinforcement) */}
            <section className="px-6 py-14 bg-lavender-50">
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="font-fraunces text-3xl sm:text-4xl font-medium mb-4">Not sure which session is right for you?</h2>
                    <p className="text-plum-soft mb-8 max-w-xl mx-auto">
                        Book a free 10-minute discovery call to discuss your needs and find the perfect path forward.
                    </p>
                    <button className="px-8 py-3 rounded-full border border-lavender-300 text-lavender-700 hover:bg-white transition-colors font-bold">
                        Book Discovery Call
                    </button>
                </div>
            </section>

        </>
    );
};

export default Services;
